import * as models from "./api/models";
import isAddressKey from "./isAddressKey";
import isSecuredDepositKey from "./isSecuredDepositKey";
import validateAddressField from "./validateAddressField";
import {
    MAX_ROUTING_NUMBER_LENGTH,
    MIN_ACCOUNT_NUMBER_LENGTH,
    MAX_ACCOUNT_NUMBER_LENGTH,
} from "../components/BankAccountInfo/BankAccountInfo";
import {MAX_CARD_LENGTH} from "../components/CardAccountInfo/CardAccountInfo";
import {EMPTY_VALUE} from "../components/Select";
import {InputValidation} from "./validateApplicationField";

const EMPTY_ARR: string[] = [];
const NOT_FOUND_VALIDATION = {isValid: true, messages: EMPTY_ARR, validationKey: null};

const SELECT_INPUTS = [models.SecuredDepositKeys.Amount, models.SecuredDepositKeys.CardType];

const REQUIRED_INPUTS = [
    models.SecuredDepositKeys.AccountNumber,
    models.SecuredDepositKeys.AbaNumber,
    models.SecuredDepositKeys.CardNumber,
    models.SecuredDepositKeys.CardExpirationDate,
    models.SecuredDepositKeys.CardName,
    models.SecuredDepositKeys.PhoneNumber,
];

const strings = {
    required: "Value is required.",
    invalidFormat: "is not a valid value.",
    ssFormat: "Social Security should be in the XXX-XX-XXXX format.",
    abaNumberFormat: "Routing number should be 9 numbers long.",
    accountNumberFormat: `Account number should be between ${MIN_ACCOUNT_NUMBER_LENGTH}-${MAX_ACCOUNT_NUMBER_LENGTH} numbers long.`,
    cardNumberFormat: "Card number should be 16 numbers long.",
    cardExpirationFormat: "Expiration Date should be in MM/YY format.",
};

const REGEX = {
    cardExpirationDate: "^(0[1-9]|1[0-2])\\/[0-9]{2}$",
    routingNumber: `^([0-9]{${MAX_ROUTING_NUMBER_LENGTH}})$`,
    accountNumber: `^([0-9]{${MIN_ACCOUNT_NUMBER_LENGTH},${MAX_ACCOUNT_NUMBER_LENGTH}})$`,
    cardNumber: `^([0-9]{${MAX_CARD_LENGTH}})$`,
    phone: /^\([0-9]{3}\) [0-9]{3}-[0-9]{4}$/,
};

export default function validateDepositField(
    name: models.SecuredDepositKeys | models.AddressKeys,
    value: string | boolean,
): InputValidation {
    if (isAddressKey(name)) {
        return validateAddressField(name, value);
    }
    const validationKey = name;
    if (!isAddressKey(name) && !isSecuredDepositKey(name)) {
        return NOT_FOUND_VALIDATION;
    }

    if (SELECT_INPUTS.find((selectName) => name === selectName)) {
        const isValid = value !== EMPTY_VALUE;
        return {
            isValid,
            messages: isValid ? EMPTY_ARR : [strings.required],
            validationKey: validationKey,
        };
    }

    const validation = {
        isValid: true,
        messages: EMPTY_ARR,
        validationKey,
    };

    if (REQUIRED_INPUTS.find((reqdName) => name === reqdName)) {
        if (typeof value === "string" && !value) {
            validation.isValid = false;
            validation.messages = [...validation.messages, strings.required];
        }
    }

    if (name === models.SecuredDepositKeys.AbaNumber && typeof value === "string" && !!value) {
        const invalid =
            value.length < MAX_ROUTING_NUMBER_LENGTH || !value.match(REGEX.routingNumber);
        if (invalid) {
            validation.isValid = false;
            validation.messages = [
                ...validation.messages,
                `"${value}" ${strings.invalidFormat}`,
                strings.abaNumberFormat,
            ];
        }
    }

    if (
        (name === models.SecuredDepositKeys.AccountNumber ||
            name === models.SecuredDepositKeys.ConfirmAccountNumber) &&
        typeof value === "string" &&
        !!value
    ) {
        const invalid =
            value.length < MIN_ACCOUNT_NUMBER_LENGTH ||
            value.length > MAX_ACCOUNT_NUMBER_LENGTH ||
            !value.match(REGEX.accountNumber);
        if (invalid) {
            validation.isValid = false;
            validation.messages = [
                ...validation.messages,
                `"${value}" ${strings.invalidFormat}`,
                strings.accountNumberFormat,
            ];
        }
    }

    if (name === models.SecuredDepositKeys.CardNumber && typeof value === "string" && !!value) {
        const invalid = value.length < MAX_CARD_LENGTH || !value.match(REGEX.cardNumber);
        if (invalid) {
            validation.isValid = false;
            validation.messages = [
                ...validation.messages,
                `"${value}" ${strings.invalidFormat}`,
                strings.cardNumberFormat,
            ];
        }
    }

    if (
        name === models.SecuredDepositKeys.CardExpirationDate &&
        typeof value === "string" &&
        !!value
    ) {
        const invalid = !value.match(REGEX.cardExpirationDate);
        if (invalid) {
            validation.isValid = false;
            validation.messages = [
                ...validation.messages,
                `"${value}" ${strings.invalidFormat}`,
                strings.cardExpirationFormat,
            ];
        }
    }

    if (name === models.SecuredDepositKeys.PhoneNumber && typeof value === "string" && !!value) {
        const invalid = !value.match(REGEX.phone);
        if (invalid) {
            validation.isValid = false;
            validation.messages = [...validation.messages, `"${value}" ${strings.invalidFormat}`];
        }
    }

    return validation;
}
